import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  FaSearch, FaChevronDown, FaChevronUp,
  FaFileInvoiceDollar, FaFileInvoice, FaTrash, FaHistory
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import useColleges from '../../../../utils/useColleges';
import './Salary.css';

const MONTHS = [
  { value: 1, label: 'January' },  { value: 2,  label: 'February' },
  { value: 3, label: 'March' },    { value: 4,  label: 'April' },
  { value: 5, label: 'May' },      { value: 6,  label: 'June' },
  { value: 7, label: 'July' },     { value: 8,  label: 'August' },
  { value: 9, label: 'September' },{ value: 10, label: 'October' },
  { value: 11, label: 'November' },{ value: 12, label: 'December' },
];

const cy = new Date().getFullYear();
const cm = new Date().getMonth() + 1;
const YEARS = [cy - 2, cy - 1, cy, cy + 1];

// Persist filters across navigation
const LS = {
  get: (k, d) => { try { const v = localStorage.getItem(k); return v !== null ? JSON.parse(v) : d; } catch { return d; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
};

const Salary = () => {
  const { user }  = useAuth();
  const isAdmin   = user?.role === 'admin' || (user?.colleges?.length > 0);
  const { colleges, loading: collegesLoading } = useColleges();
  const navigate  = useNavigate();
  const basePath  = user?.role === 'collegeadmin' ? '/collegeadmin' : '/admin';

  const [selectedCollege, setSelectedCollege] = useState(() => LS.get('sal_college', ''));
  const [selectedYear,    setSelectedYear]    = useState(() => LS.get('sal_year',    cy));
  const [selectedMonth,   setSelectedMonth]   = useState(() => LS.get('sal_month',   cm));
  const [selectedDept,    setSelectedDept]    = useState('');
  const [deptSearchInput, setDeptSearchInput] = useState('All Departments');
  const [showDeptDrop,    setShowDeptDrop]    = useState(false);

  const [departments, setDepartments] = useState([]);
  const [employees,   setEmployees]   = useState([]);
  const [attendance,  setAttendance]  = useState({});
  const [salaries,    setSalaries]    = useState({});
  const [expandedRow, setExpandedRow] = useState(null);

  const deptDropRef = useRef(null);

  // Persist filters
  useEffect(() => { LS.set('sal_college', selectedCollege); }, [selectedCollege]);
  useEffect(() => { LS.set('sal_year',    selectedYear);    }, [selectedYear]);
  useEffect(() => { LS.set('sal_month',   selectedMonth);   }, [selectedMonth]);

  // Auto-set college
  useEffect(() => {
    if (!isAdmin && colleges.length > 0) setSelectedCollege(String(colleges[0]._id));
  }, [isAdmin, colleges]);
  useEffect(() => {
    if (isAdmin && colleges.length === 1) setSelectedCollege(String(colleges[0]._id));
  }, [isAdmin, colleges]);

  // Load departments & employees
  useEffect(() => {
    if (!selectedCollege) { setDepartments([]); setEmployees([]); return; }
    api.get('/departments', { params: { college: selectedCollege } })
      .then(r => setDepartments(r.data.data || [])).catch(() => {});
    api.get('/employees', { params: { limit: 500 } })
      .then(r => setEmployees((r.data.data || []).filter(e =>
        String(e.college?._id || e.college) === String(selectedCollege) && e.isActive !== false
      ))).catch(() => {});
  }, [selectedCollege]);

  // Load salary & attendance
  useEffect(() => {
    if (!selectedCollege) { setSalaries({}); setAttendance({}); return; }
    api.get('/salary', { params: { college: selectedCollege, month: selectedMonth, year: selectedYear } })
      .then(r => {
        const map = {};
        (r.data.data || []).forEach(s => { map[String(s.employee?._id || s.employee)] = s; });
        setSalaries(map);
      }).catch(() => setSalaries({}));
    api.get('/attendance', { params: { college: selectedCollege, month: selectedMonth, year: selectedYear } })
      .then(r => {
        const map = {};
        (r.data.data || []).forEach(rec => {
          if (rec.presentDays > 0) map[String(rec.employee?._id || rec.employee)] = rec;
        });
        setAttendance(map);
      }).catch(() => setAttendance({}));
  }, [selectedCollege, selectedMonth, selectedYear]);

  // Close dept dropdown
  useEffect(() => {
    const h = e => {
      if (deptDropRef.current && !deptDropRef.current.contains(e.target)) {
        setShowDeptDrop(false);
        setDeptSearchInput(selectedDept ? departments.find(d => d._id === selectedDept)?.name || 'All Departments' : 'All Departments');
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [selectedDept, departments]);

  const filteredDepts = useMemo(() => {
    const q = deptSearchInput.toLowerCase();
    if (q === 'all departments' || q === '') return departments;
    return departments.filter(d => d.name.toLowerCase().includes(q));
  }, [deptSearchInput, departments]);

  const displayedEmployees = useMemo(() => {
    let list = employees.filter(e => attendance[String(e._id)]);
    if (selectedDept) list = list.filter(e => String(e.department?._id || e.department) === selectedDept);
    return list;
  }, [employees, attendance, selectedDept]);

  const daysInMonth = useMemo(() => new Date(selectedYear, selectedMonth, 0).getDate(), [selectedYear, selectedMonth]);
  const collegeName = useMemo(() => colleges.find(c => String(c._id) === String(selectedCollege))?.name || '', [colleges, selectedCollege]);

  // Create Salary → navigate to CreateSalary page (no payment mode)
  const handleCreate = (emp) => {
    const attRec  = attendance[String(emp._id)];
    const present = Math.min(attRec?.totalPresent || 0, 30);
    const absent  = present >= 30 ? 0 : Math.max(0, 30 - present);
    navigate(`${basePath}/salary/create`, {
      state: {
        employee:       emp,
        collegeName,
        selectedYear,
        selectedMonth,
        daysInMonth:    30,
        presentDays:    present,
        absentDays:     absent,
        monthlySalary:  emp.monthlySalary  || emp.salary || 0,
        pfDeduction:    emp.pfDeduction    || 0,
        esiDeduction:   emp.esiDeduction   || 0,
        netDailySalary: emp.netDailySalary || 0,
      }
    });
  };

  // Pay → navigate to PaySalary page (fetches draft, has payment mode)
  const handlePay = (sal) => {
    navigate(`${basePath}/salary/pay`, {
      state: { salaryId: sal._id, collegeName }
    });
  };

  // Receipt
  const handleReceipt = (sal) => {
    navigate(`${basePath}/salary/receipt`, { state: { salary: sal, collegeName } });
  };

  // Delete draft only
  const handleDelete = async (salaryId) => {
    const result = await Swal.fire({
      title: 'Delete Salary?', text: 'This will remove the draft salary record.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#ef4444', cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete',
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/salary/${salaryId}`);
      setSalaries(prev => {
        const u = { ...prev };
        const k = Object.keys(u).find(key => u[key]._id === salaryId);
        if (k) delete u[k];
        return u;
      });
      Swal.fire('Deleted!', 'Draft removed.', 'success');
    } catch { Swal.fire('Error', 'Failed to delete.', 'error'); }
  };

  return (
    <div className="salary-of-employee-container">
      <h2 className="salary-of-employee-title">Manage Employee Salary</h2>

      {/* Filters */}
      <div className="salary-of-employee-filters-wrapper">
        {isAdmin && (
          <div className="salary-of-employee-filter-group">
            <label className="salary-of-employee-filter-label">College</label>
            <select className="salary-of-employee-select-input" value={selectedCollege} onChange={e => setSelectedCollege(e.target.value)}>
              <option value="">-- Select College --</option>
              {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
        )}
        <div className="salary-of-employee-filter-group">
          <label className="salary-of-employee-filter-label">Year</label>
          <select className="salary-of-employee-select-input" value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="salary-of-employee-filter-group">
          <label className="salary-of-employee-filter-label">Month</label>
          <select className="salary-of-employee-select-input" value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}>
            {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div className="salary-of-employee-filter-group" ref={deptDropRef}>
          <label className="salary-of-employee-filter-label">Department</label>
          <div className="salary-of-employee-searchable-dropdown">
            <FaSearch className="salary-of-employee-search-icon" />
            <input type="text" className="salary-of-employee-dropdown-input"
              value={deptSearchInput}
              onChange={e => { setDeptSearchInput(e.target.value); setShowDeptDrop(true); }}
              onClick={() => { setDeptSearchInput(''); setShowDeptDrop(true); }}
              placeholder="Search department..." />
            <FaChevronDown className="salary-of-employee-chevron-icon" />
            {showDeptDrop && (
              <div className="salary-of-employee-dropdown-list">
                <ul>
                  <li onClick={() => { setSelectedDept(''); setDeptSearchInput('All Departments'); setShowDeptDrop(false); }}>All Departments</li>
                  {filteredDepts.map(d => (
                    <li key={d._id} onClick={() => { setSelectedDept(d._id); setDeptSearchInput(d.name); setShowDeptDrop(false); }}>{d.name}</li>
                  ))}
                  {filteredDepts.length === 0 && <li className="salary-of-employee-dropdown-empty">No departments found</li>}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="salary-of-employee-table-wrapper">
        <table className="salary-of-employee-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th className="salary-of-employee-align-left">Employee Name</th>
              <th className="salary-of-employee-col-dept">Department</th>
              <th className="salary-of-employee-col-salary">Monthly Salary</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedEmployees.length === 0 ? (
              <tr><td colSpan="5" className="salary-of-employee-empty-msg">
                {selectedCollege ? 'No employees with attendance found.' : 'Please select a college.'}
              </td></tr>
            ) : displayedEmployees.map((emp, i) => {
              const sal      = salaries[String(emp._id)];
              const isPaid   = sal?.isPaid === true;
              const isDraft  = sal && !isPaid;
              const isExpanded = expandedRow === emp._id;

              return (
                <React.Fragment key={emp._id}>
                  <tr className="salary-of-employee-main-row">
                    <td>{i + 1}</td>
                    <td className="salary-of-employee-align-left">
                      <div className="salary-of-employee-name-wrapper"
                        onClick={() => setExpandedRow(isExpanded ? null : emp._id)}>
                        <span>{emp.name}</span>
                        <button className="salary-of-employee-expand-btn">
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                    </td>
                    <td className="salary-of-employee-col-dept">
                      <span className="salary-of-employee-badge-dept">{emp.department?.name || '—'}</span>
                    </td>
                    <td className="salary-of-employee-col-salary">
                      <span className="salary-of-employee-salary-text">₹ {(emp.monthlySalary || emp.salary || 0).toLocaleString()}</span>
                    </td>
                    <td>
                      {isPaid ? (
                        /* PAID — badge + receipt only, NO delete */
                        <div className="salary-of-employee-action-group">
                          <span className="salary-of-employee-badge-paid">✓ Paid</span>
                          <button className="salary-of-employee-icon-btn receipt" title="Receipt" onClick={() => handleReceipt(sal)}>
                            <FaFileInvoice />
                          </button>
                        </div>
                      ) : isDraft ? (
                        /* DRAFT — "Salary Created" badge + Pay button + Delete */
                        <div className="salary-of-employee-action-group">
                          <span className="salary-of-employee-badge-draft">Salary Created</span>
                          <button className="salary-of-employee-btn-pay" onClick={() => handlePay(sal)}>
                            Pay
                          </button>
                          <button className="salary-of-employee-icon-btn delete" title="Delete Draft" onClick={() => handleDelete(sal._id)}>
                            <FaTrash />
                          </button>
                        </div>
                      ) : (
                        /* NO RECORD — Create Salary */
                        <button className="salary-of-employee-btn-create" onClick={() => handleCreate(emp)}>
                          <FaFileInvoiceDollar /> Create Salary
                        </button>
                      )}
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="salary-of-employee-expanded-row">
                      <td colSpan="5">
                        <div className="salary-of-employee-expanded-content">
                          <div className="salary-of-employee-expanded-item">
                            <span className="salary-of-employee-exp-label">Department</span>
                            <span className="salary-of-employee-exp-value">{emp.department?.name || '—'}</span>
                          </div>
                          <div className="salary-of-employee-expanded-item">
                            <span className="salary-of-employee-exp-label">Monthly Salary</span>
                            <span className="salary-of-employee-exp-value">₹ {(emp.monthlySalary || emp.salary || 0).toLocaleString()}</span>
                          </div>
                          {sal && (
                            <div className="salary-of-employee-expanded-item">
                              <span className="salary-of-employee-exp-label">Net Payable</span>
                              <span className="salary-of-employee-exp-value">₹ {(sal.netSalary || 0).toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Salary;
